import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { Direction, Locale } from "@nelurio/contracts";
import { displayTag, readRegion, regionToCalendar, storeRegion, type CalendarSystem } from "./calendar";

export { displayTag, persianMonthDays, persianMonthStart, persianParts, readRegion, regionToCalendar, storeRegion } from "./calendar";
export type { CalendarSystem } from "./calendar";

import de from "./locales/de-DE.json";
import en from "./locales/en.json";
import fa from "./locales/fa-IR.json";
import uk from "./locales/uk-UA.json";
import ar from "./locales/ar.json";
import tr from "./locales/tr-TR.json";
import fr from "./locales/fr-FR.json";
import es from "./locales/es-ES.json";
import it from "./locales/it-IT.json";
import pl from "./locales/pl-PL.json";

export const languages: Array<{ code: Locale; label: string; nativeLabel: string; direction: Direction }> = [
  { code: "de-DE", label: "German", nativeLabel: "Deutsch", direction: "ltr" },
  { code: "en", label: "English", nativeLabel: "English", direction: "ltr" },
  { code: "fa-IR", label: "Persian", nativeLabel: "فارسی", direction: "rtl" },
  { code: "uk-UA", label: "Ukrainian", nativeLabel: "Українська", direction: "ltr" },
  { code: "ar", label: "Arabic", nativeLabel: "العربية", direction: "rtl" },
  { code: "tr-TR", label: "Turkish", nativeLabel: "Türkçe", direction: "ltr" },
  { code: "fr-FR", label: "French", nativeLabel: "Français", direction: "ltr" },
  { code: "es-ES", label: "Spanish", nativeLabel: "Español", direction: "ltr" },
  { code: "it-IT", label: "Italian", nativeLabel: "Italiano", direction: "ltr" },
  { code: "pl-PL", label: "Polish", nativeLabel: "Polski", direction: "ltr" }
];

type Messages = Record<string, string>;
const messages: Record<Locale, Messages> = {
  "de-DE": de,
  en,
  "fa-IR": fa,
  "uk-UA": uk,
  ar,
  "tr-TR": tr,
  "fr-FR": fr,
  "es-ES": es,
  "it-IT": it,
  "pl-PL": pl
};

interface I18nContextValue {
  locale: Locale;
  direction: Direction;
  setLocale: (locale: Locale) => void;
  /** IANA timezone driving the display calendar (Iran → Persian calendar). */
  region: string;
  calendar: CalendarSystem;
  setRegion: (timezone: string) => void;
  t: (key: string, values?: Record<string, string | number>) => string;
  formatDate: (value: Date | string, options?: Intl.DateTimeFormatOptions) => string;
  /** Day-of-month number rendered in the active calendar system. */
  formatDayNumber: (value: Date | string) => string;
  formatNumber: (value: number, options?: Intl.NumberFormatOptions) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

function normalizeLocale(value?: string | null): Locale {
  if (value && languages.some((language) => language.code === value)) return value as Locale;
  const short = value?.toLowerCase();
  if (short?.startsWith("fa")) return "fa-IR";
  if (short?.startsWith("de")) return "de-DE";
  if (short?.startsWith("uk")) return "uk-UA";
  if (short?.startsWith("ar")) return "ar";
  if (short?.startsWith("tr")) return "tr-TR";
  if (short?.startsWith("fr")) return "fr-FR";
  if (short?.startsWith("es")) return "es-ES";
  if (short?.startsWith("it")) return "it-IT";
  if (short?.startsWith("pl")) return "pl-PL";
  return "en";
}

export function I18nProvider({ children, defaultLocale }: { children: React.ReactNode; defaultLocale?: Locale }) {
  const [locale, updateLocale] = useState<Locale>(() => {
    const stored = typeof window !== "undefined" ? window.localStorage.getItem("nelurio.locale") : null;
    return normalizeLocale(stored || defaultLocale || (typeof navigator !== "undefined" ? navigator.language : "en"));
  });

  const direction = languages.find((language) => language.code === locale)?.direction ?? "ltr";

  const [region, updateRegion] = useState<string>(() => readRegion());
  const calendar = useMemo(() => regionToCalendar(region), [region]);

  const setRegion = useCallback((timezone: string) => {
    storeRegion(timezone);
    updateRegion(timezone);
  }, []);

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = direction;
    document.documentElement.dataset.locale = locale;
    document.documentElement.dataset.calendar = calendar;
    window.localStorage.setItem("nelurio.locale", locale);
  }, [calendar, direction, locale]);

  const setLocale = useCallback((next: Locale) => updateLocale(normalizeLocale(next)), []);

  const t = useCallback((key: string, values?: Record<string, string | number>) => {
    let value = messages[locale][key] ?? messages.en[key] ?? key;
    if (values) {
      for (const [name, replacement] of Object.entries(values)) {
        value = value.replaceAll(`{{${name}}}`, String(replacement));
      }
    }
    return value;
  }, [locale]);

  const formatDate = useCallback((value: Date | string, options?: Intl.DateTimeFormatOptions) => {
    const date = value instanceof Date ? value : new Date(value);
    return new Intl.DateTimeFormat(displayTag(locale, calendar), options ?? { year: "numeric", month: "short", day: "numeric" }).format(date);
  }, [calendar, locale]);

  const formatDayNumber = useCallback((value: Date | string) => {
    const date = value instanceof Date ? value : new Date(value);
    return new Intl.DateTimeFormat(displayTag(locale, calendar), { day: "numeric" }).format(date);
  }, [calendar, locale]);

  const formatNumber = useCallback((value: number, options?: Intl.NumberFormatOptions) => {
    return new Intl.NumberFormat(displayTag(locale, calendar), options).format(value);
  }, [calendar, locale]);

  const contextValue = useMemo(
    () => ({ locale, direction, setLocale, region, calendar, setRegion, t, formatDate, formatDayNumber, formatNumber }),
    [calendar, direction, formatDate, formatDayNumber, formatNumber, locale, region, setLocale, setRegion, t]
  );

  return <I18nContext.Provider value={contextValue}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const value = useContext(I18nContext);
  if (!value) throw new Error("useI18n must be used inside I18nProvider");
  return value;
}
